import { now, offsetDateTimeToMs } from "@freightsimple/time/helpers";
import auth0 from "auth0-js";
import { auth0Creds } from "@/Environment";

interface SignInWithPasswordParams {
  email: string;
  password: string;
  /** Reports the failure to the form; cleared on success before authenticating. */
  setGeneralErrorMessage: (message: string | undefined) => void;
  /** Fired once Auth0 accepts the credentials, before the token is exchanged. */
  onSignedIn: () => void;
  /** Exchanges the Auth0 access token for an authenticated session. Without
   *  expiresAt the session has no known end. */
  authenticate: (
    accessToken: string,
    expiresAt: number | undefined,
  ) => Promise<unknown>;
}

/**
 * Signs a freshly-created account in with the password it was created with,
 * wrapping Auth0's callback API in a promise.
 *
 * Two distinct failures, deliberately reported differently: Auth0 rejecting the
 * credentials gives a description worth showing, while a failure exchanging the
 * token afterwards is ours and gets a generic message — the customer can't act
 * on the detail either way, and the specific text would be misleading.
 */
export async function authenticationSignInWithPassword(
  params: SignInWithPasswordParams,
): Promise<void> {
  const auth = new auth0.WebAuth({
    domain: auth0Creds().domain,
    clientID: auth0Creds().clientId,
    scope: "openid profile email",
  });

  return new Promise<void>(function (resolve, reject) {
    auth.client.login(
      {
        username: params.email,
        password: params.password,
        realm: "Username-Password-Authentication",
        audience: auth0Creds().audience,
      },
      async function (error, result) {
        if (error) {
          console.error("auth0 error", { error, result });
          params.setGeneralErrorMessage(error.description);
          reject(new Error(`Login Problem : ${error.description}`));
          return;
        }

        params.setGeneralErrorMessage(undefined);
        params.onSignedIn();

        try {
          const expiresAt = result.expiresIn
            ? offsetDateTimeToMs(now()) + result.expiresIn * 1000
            : undefined;
          await params.authenticate(result.accessToken, expiresAt);
          resolve();
        } catch {
          params.setGeneralErrorMessage("Something went wrong");
          reject(new Error("Something went wrong"));
        }
      },
    );
  });
}
